/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useCallback, useEffect, useState } from 'react';
import './ResponsiveTable.scss';
import BsTable from 'react-bootstrap/Table';
import { v1 as uuid } from 'uuid';
import { Icon } from '../icon/Icon';
import { Sort } from '../sort/Sort';
import classNames from 'classnames';

export enum ETableColumnAlign {
  START = 'start',
  CENTER = 'center',
  END = 'end',
}

export interface ResponsiveTableColumn<T> {
  title: string;
  objectKey: keyof T;
  width?: string;
  minWidth?: string;
  verticalAlign?: ETableColumnAlign;
  horizontalAlign?: ETableColumnAlign;
  disableSort?: boolean;
  hideBorder?: boolean;
  customSortFunction?: (indexA: number, indexB: number, direction: ESortDirection, objectKey: keyof T) => number;
}

interface ResponsiveTableProps<T> {
  className?: string;
  columns: ResponsiveTableColumn<T>[];
  data: T[];
  firstRow?: T;
  windowBreakPointPx?: number;
  sortByDefault?: ResponsiveTableColumn<T>;
  sortDirectionDefault?: ESortDirection;
  whiteRows?: boolean;
  largeCellPadding?: boolean;
  onSortChange?: (direction: ESortDirection, by?: ResponsiveTableColumn<T>) => void;
  onDisplayDataChange?: (data: T[]) => void;
  filterFunction?: (item: T) => boolean;
}

export enum ESortDirection {
  ASCENDING = 'ascending',
  DESCENDING = 'descending',
}

export function ResponsiveTable<T>({ className, columns, data, firstRow, windowBreakPointPx, sortByDefault, sortDirectionDefault, whiteRows, largeCellPadding, onSortChange, onDisplayDataChange, filterFunction }: ResponsiveTableProps<T>): JSX.Element {
  const [keyMap] = useState<WeakMap<any, string>>(new WeakMap());
  const [mobileMode, setMobileMode] = useState<boolean>(true);
  const [sortedBy, setSortedBy] = useState<ResponsiveTableColumn<T>|undefined>(sortByDefault);
  const [sortDirection, setSortDirection] = useState<ESortDirection>(sortDirectionDefault ?? ESortDirection.DESCENDING);
  const [sortedData, setSortedData] = useState<T[]>([...data]);

  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortDirection, sortedBy);
    }
  }, [sortDirection, sortedBy, onSortChange]);

  useEffect(() => {
    if (onDisplayDataChange) {
      onDisplayDataChange([...sortedData]);
    }
  }, [sortedData, onDisplayDataChange]);

  const sortList = useCallback((a: T, b: T, dataToSort: T[]): number => {
    if (!sortedBy) {
      return 0; // Nothing to sort, why are we even here?
    }
    const { objectKey, customSortFunction } = sortedBy;
    const sortDesc = sortDirection === ESortDirection.DESCENDING;
    const valueA = a[objectKey];
    const valueB = b[objectKey];
    if (customSortFunction) {
      const indexOfA = dataToSort.indexOf(a);
      const indexOfB = dataToSort.indexOf(b);
      return customSortFunction(indexOfA, indexOfB, sortDirection, objectKey);
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortDesc ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
    } else if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortDesc ? valueB - valueA : valueA - valueB;
    } else {
      return 0; // No idea how to sort you...
    }
  }, [sortDirection, sortedBy]);

  useEffect(() => {
    const dataCopy = [...data].filter(item => (filterFunction ? filterFunction(item) : true));
    if (!sortedBy) {
      // nothing to sort, all items are in order
      setSortedData(dataCopy);
    } else {
      dataCopy.sort((a, b) => sortList(a, b, dataCopy));
      setSortedData(dataCopy);
    }
  }, [data, sortedBy, sortDirection, sortList, filterFunction]);

  const resizeUpdate = useCallback(() => {
    const { innerWidth } = window;
    const isMobileMode = innerWidth <= (windowBreakPointPx ?? 767); // 767 = $tablet-breakpoint in _library.scss
    if (isMobileMode !== mobileMode) {
      setMobileMode(isMobileMode);
    }
  }, [windowBreakPointPx, mobileMode]);

  useEffect(() => {
    resizeUpdate();
    window.addEventListener('resize', resizeUpdate);
    return () => window.removeEventListener('resize', resizeUpdate);
  }, [resizeUpdate]);

  const getObjectId = (object: ResponsiveTableColumn<T>|T): string => {
    const hasId = keyMap.get(object);
    if (hasId !== undefined) {
      return hasId;
    } else {
      const newId = uuid();
      keyMap.set(object, newId);
      return newId;
    }
  };

  const renderHeaderSortIcon = (columnLayout: ResponsiveTableColumn<T>): JSX.Element|null => {
    const columnIsSorted = sortedBy?.objectKey === columnLayout.objectKey;
    if (columnIsSorted && sortDirection === ESortDirection.DESCENDING) {
      return <span className="header-item-sort__icon header-item-sort__icon--desc"><Icon icon="down"/></span>;
    } else if (columnIsSorted && sortDirection === ESortDirection.ASCENDING) {
      return <span className="header-item-sort__icon header-item-sort__icon--asc"><Icon icon="down"/></span>;
    } else {
      return <span className="header-item-sort__icon header-item-sort__icon--none"><Icon icon="down"/></span>;
    }
  };

  const setSortOrder = (columnLayout: ResponsiveTableColumn<T>): void => {
    // On first click: start sorting as descending
    // On second click: switch sorting to ascending
    // On third click: reset sorting on this column
    if (sortedBy?.objectKey === columnLayout.objectKey) {
      switch (sortDirection) {
        case ESortDirection.DESCENDING:
          setSortDirection(ESortDirection.ASCENDING);
          break;
        case ESortDirection.ASCENDING:
        default:
          setSortDirection(ESortDirection.DESCENDING);
          setSortedBy(sortByDefault);
      }
    } else {
      setSortedBy(columnLayout);
      setSortDirection(sortDirectionDefault ?? ESortDirection.DESCENDING);
    }
  };

  const setSortOrderViaMobileMenu = (selected: ResponsiveTableColumn<T>, direction?: ESortDirection): void => {
    setSortedBy(selected);
    setSortDirection(direction ?? ESortDirection.DESCENDING);
  };

  const getColumnClassNames = (column: ResponsiveTableColumn<T>): string => classNames(
    'responsive-table-column',
    {
      [`responsive-table-column--vertical-align-${column.verticalAlign || ''}`]: column.verticalAlign,
      [`responsive-table-column--horizontal-align-${column.horizontalAlign || ''}`]: column.horizontalAlign,
      'responsive-table-column--hide-border': column.hideBorder,
    }
  );

  const renderDesktopMode = (): JSX.Element => (
    <BsTable className={classNames('responsive-table', { 'responsive-table--white-rows': whiteRows, 'responsive-table--large-cell-padding': largeCellPadding }, className)}>
      <thead className="responsive-table__head">
        <tr>
          {
            columns.map(column => {
              const { title, width, minWidth } = column;
              const style = { width, minWidth };
              return <th key={getObjectId(column)} className={`responsive-table__head-item ${getColumnClassNames(column)}`} style={style}>
                { column.disableSort ? title : <button className="responsive-table__head-item-sort-button" onClick={() => setSortOrder(column)}>
                  <span className="head-item-sort__title">{title}</span>{renderHeaderSortIcon(column)}
                </button> }
              </th>;
            })
          }
        </tr>
      </thead>
      <tbody className="responsive-table__body">
        { firstRow &&
          <tr>
            { columns.map((column, index) => <td key={`first-row-${index}`} className={getColumnClassNames(column)}>{firstRow[column.objectKey]}</td>) }
          </tr>
        }
        {
          sortedData.map(rowData => (
            <tr key={getObjectId(rowData)}>
              {
                columns.map(column => <td key={`${getObjectId(column)}${getObjectId(rowData)}`} className={getColumnClassNames(column)}>
                  {rowData[column.objectKey]}
                </td>)
              }
            </tr>
          ))
        }
      </tbody>

    </BsTable>
  );

  const renderMobileMode = (): JSX.Element => {
    const fullClassName = `responsive-card-container ${className || ''}`;
    return (
      <div className={fullClassName}>
        <Sort columns={columns} selected={sortedBy} selectedDirection={sortDirection} onChange={setSortOrderViaMobileMenu} />
        {
          firstRow &&
          <div className="responsive-card-container__responsive-card responsive-card">
            <table className="responsive-card__table">
              <tbody>
                {
                  columns.map((column, index) => (
                    <tr className="responsive-card__item" key={`first-row-${index}`}>
                      <th className="responsive-card__item-title">{column.title}</th>
                      <td className="responsive-card__item-value">{firstRow[column.objectKey]}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        }
        {
          sortedData.map(cardData => (
            <div className="responsive-card-container__responsive-card responsive-card" key={getObjectId(cardData)}>
              <table className="responsive-card__table">
                <tbody>
                  {
                    columns.map(column => (
                      <tr className="responsive-card__item" key={`${getObjectId(column)}${getObjectId(cardData)}`}>
                        <th className="responsive-card__item-title">{column.title}</th>
                        <td className="responsive-card__item-value">{cardData[column.objectKey]}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          ))
        }
      </div>
    );
  };

  if (mobileMode) {
    return renderMobileMode();
  } else {
    return renderDesktopMode();
  }
}
